import { type Answer, type Condition, ConditionLogicalGroup, ConditionStatement, type Question } from '../../types';
import type { QuestionModel } from '../QuestionModel';

type CheckConditionFn = (selectedAnswers: Answer[], conditionAnswers: Set<Condition['condition_answer_list'][0]>) => boolean;

const checkConditionFns: Record<ConditionStatement, CheckConditionFn> = {
  [ConditionStatement.ANY_EQUAL]: (selectedAnswers, conditionAnswers) => {
    return selectedAnswers.some(({ id }) => conditionAnswers.has(id));
  },
  [ConditionStatement.FULL_MATCHED]: (selectedAnswers, conditionAnswers) => {
    if (selectedAnswers.length !== conditionAnswers.size) {
      return false;
    }

    return selectedAnswers.every(({ id }) => conditionAnswers.has(id));
  },
  [ConditionStatement.FULL_PART_MATCHED]: (selectedAnswers, conditionAnswers) => {
    return selectedAnswers.every(({ id }) => conditionAnswers.has(id));
  }
};

type CheckRelationFn = (conditionResults: boolean[]) => boolean;

const checkRelationFns: Record<ConditionLogicalGroup, CheckRelationFn> = {
  [ConditionLogicalGroup.AND]: (conditionResults) => {
    return conditionResults.every((result) => result);
  },
  [ConditionLogicalGroup.OR]: (conditionResults) => {
    return conditionResults.some((result) => result);
  }
};

const checkConditions = (conditions: Condition[], questionHistory: QuestionModel[]) => {
  const answerHistory = new Map<Question['id'], QuestionModel['selectedAnswers']>();
  questionHistory.forEach((qm) => {
    answerHistory.set(qm.question.id, qm.selectedAnswers);
  });

  return conditions.map((cond) => {
    const selectedAnswers = answerHistory.get(cond.condition_question_id);

    if (!selectedAnswers?.length) {
      // это возможно, если вопрос необязательный и юзер на него не ответил
      return false;
    }

    const conditionAnswers = new Set(cond.condition_answer_list);

    return checkConditionFns[cond.condition_statement](selectedAnswers, conditionAnswers);
  });
};

/**
 * Вся логика выбора подходящего перехода к следующему вопросу.
 */
export const getSuitableRelation = (currentQuestionModel: QuestionModel, questionHistory: QuestionModel[]) => {
  const { form_question_rel: relations } = currentQuestionModel.question;

  const relationCandidates = relations
    .filter((rel) => {
      if (rel.is_default) {
        return true;
      }
      const conditionResults = checkConditions(rel.form_questions_rel_conditions, questionHistory);
      return rel.condition_logical_group && checkRelationFns[rel.condition_logical_group](conditionResults);
    })
    .sort((rel1, rel2) => {
      // ставим переход по умолчанию на индекс 0
      if (rel1.is_default) {
        return -1;
      }
      if (rel2.is_default) {
        return 1;
      }

      // переход с меньшим номером наиболее приоритетный
      return rel1.relation_number - rel2.relation_number;
    });

  const suitableRelation = relationCandidates.length > 1 ? relationCandidates[1] : relationCandidates[0];

  return suitableRelation;
};
